import { useTranslation } from "react-i18next";

import { customToast } from "../../../components/notify";
import { useGetCharacterQuery, useUpdateCharacterMutation } from "../services";
import { ICharacterSettingInput, IUpdateCharacterReq } from "../types";

interface IProps {
    characterId: string;
    projectId: string;
}
const useCharacterSettingSelectInput = ({ characterId, projectId }: IProps) => {
    const { t } = useTranslation();
    const { data: characters, isLoading: getCharacterLoading } = useGetCharacterQuery({ id: characterId });
    const [updateCharacter] = useUpdateCharacterMutation();

    const preUpdateCharacter = characters?.data?.character;

    const updateCharacterData = {
        voice_id: preUpdateCharacter?.voice_id ?? "",
        name: preUpdateCharacter?.name ?? "",
        greeting: preUpdateCharacter?.greeting ?? "",
        img_gen_enabled: preUpdateCharacter?.img_gen_enabled ?? false,
        copyable: preUpdateCharacter?.copyable ?? false,
        avatar_file_name: preUpdateCharacter?.avatar_file_name,
        restrict_response: preUpdateCharacter?.restrict_response ?? false,
        description: preUpdateCharacter?.description,
        pitch: preUpdateCharacter?.pitch ?? 0,
        speed: preUpdateCharacter?.speed ?? 0,
        prompt: preUpdateCharacter?.prompt,
    };

    const fetchUpdate = async (reqData: IUpdateCharacterReq): Promise<boolean> => {
        try {
            await updateCharacter(reqData).unwrap();
            customToast.success(t("COMMON.Successfully"));
            return true;
        } catch (error) {
            if (error instanceof Error) {
                customToast.error(error.message);
            } else {
                customToast.error("Failed");
            }
            return false;
        }
    };

    const handleFormSubmit = async (validatedData: ICharacterSettingInput): Promise<boolean> => {
        if ((validatedData.service_type || validatedData.gpt_model_id) && characterId && projectId) {
            const normalizedRagSetting = validatedData.rag_setting
                ? {
                      // 注意：enable_max_doc_segments 不送到 API（僅用於前端 UI 控制）
                      dense_top_k: validatedData.rag_setting.dense_top_k,
                      score_threshold: validatedData.rag_setting.score_threshold,
                      sparse_top_k: validatedData.rag_setting.sparse_top_k,
                      top_k: validatedData.rag_setting.top_k,
                      max_doc_segments: validatedData.rag_setting.max_doc_segments ?? -1,
                      use_references: validatedData.rag_setting.use_references ?? false,
                  }
                : undefined;

            return await fetchUpdate({
                ...updateCharacterData,
                id: characterId,
                project_id: projectId,
                service_type: Number(validatedData.service_type),
                gpt_model: Number(validatedData.gpt_model_id) ?? 0,
                rag_model: Number(validatedData.rag_model_id) ?? 0,
                enable_context: validatedData.enable_context ?? false,
                max_context_words: Number(validatedData.max_context_words) ?? 500,
                enable_hint: validatedData.enable_hint ?? false,
                task_type: Number(validatedData.task_type) ?? 0,
                hint_number: validatedData.hint_number ?? 2,
                temperature: validatedData.temperature ?? 0,
                primary_language: validatedData.primary_language ?? "auto",
                rag_setting: normalizedRagSetting,
            });
        }
        return false;
    };

    return { getCharacterLoading, updateCharacterData, fetchUpdate, handleFormSubmit };
};

export default useCharacterSettingSelectInput;
